import React, { PureComponent } from 'react'
import { List, is, fromJS, Set } from 'immutable'
import { curry } from 'lodash'
import moment from 'moment'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'

import { getDisplayName } from 'shared-web/src/helpers/CustomerHelpers'
import HoverAvatar from 'components/shared/HoverAvatar'

import TagSelect from 'components/shared/TagSelect'
import UserSelect from 'components/shared/UserSelect'
import TagList from 'components/TagList'
import { fetchTimeZone } from 'app/api/TimeZoneAPI'
import LocationSearch from 'components/shared/LocationSearch'
import { patchOperations } from 'utils/PatchHelper'

import * as ConversationActions from 'app/modules/conversation/actions'
import * as ConversationSelectors from 'app/modules/conversation/selectors'
import * as EndUserActions from 'app/modules/endUser/actions'
import * as EndUserAttributesActions from 'app/modules/endUser/attribute/actions'
import * as EndUserAttributesSelectors from 'app/modules/endUser/attribute/selectors'
import * as EndUserSegmentsActions from 'app/modules/endUser/segment/actions'
import * as EndUserSegmentsSelectors from 'app/modules/endUser/segment/selectors'
import * as OrgSelector from 'app/modules/org/selectors'
import * as OrgActions from 'app/modules/org/actions'
import * as UserSelector from 'app/modules/user/selectors'

const LABEL = Object.freeze({
  name: 'name',
  email: 'email',
  phone: 'phone number',
  timeZone: 'time zone',
  displayLocation: 'location',
  employmentTitle: 'title',
  employmentRole: 'role',
  employmentSeniority: 'seniority',
  linkedinHandle: 'linkedin',
  twitterHandle: 'twitter',
  bio: 'twitter bio',
  githubHandle: 'github',
  facebookHandle: 'facebook',
  tags: 'tags',
  conversations: '# conversations',
  segments: '# segments',
  externalId: 'userId',
  _account_owner: 'account owner',
})

const DEFAULT_ATTRIBUTES = Set(Object.keys(LABEL))
const DEFAULT_ENABLED_ATTRIBUTES = Set.of('_account_owner', 'tags', 'email', 'phone', 'location', 'twitter', 'linkedin', 'title', 'time zone', '_classification')
const UNEDITABLE_ATTRIBUTES = Set.of('city', 'country', 'latitude', 'longitude', 'country', '_classification')

export default ComposedComponent => {
  class GetRows extends PureComponent {
    state = {
      endUser: fromJS(this.props.endUser),
      enabledAttributes: this.getEnabledAttributes(),
    }

    componentWillMount() {
      if (!this.props.endUserAttributesFetched) {
        this.props.actions.fetchEndUserAttributes()
      }
      if (!this.props.segmentFetchedForEndUser) {
        this.props.actions.fetchSegments({ id: this.state.endUser.get('id') })
      }
      if (!this.props.endUserConversationCountFetched) {
        this.props.actions.fetchConversationsCountForEndUserId({ id: this.state.endUser.get('id') })
      }
    }

    componentWillReceiveProps(nextProps) {
      if (nextProps.endUser.id !== this.props.endUser.id) {
        this.setState({
          endUser: fromJS(nextProps.endUser),
        })
      }

      if (nextProps.endUserAttributesFetched && !this.props.endUserAttributesFetched) {
        this.setState({
          enabledAttributes: this.getEnabledAttributes(),
        })
      }
    }

    hasChanged() {
      const endUserChanged = !is(this.state.endUser, fromJS(this.props.endUser))
      const enabledAttributesChanged = !is(
        this.state.enabledAttributes,
        this.getEnabledAttributes())
      return endUserChanged || enabledAttributesChanged
    }

    createAttributeCheckBoxChange = (label) => () => {
      const { enabledAttributes } = this.state
      if (enabledAttributes.has(label)) {
        this.setState({ enabledAttributes: enabledAttributes.remove(label) })
      } else {
        this.setState({ enabledAttributes: enabledAttributes.add(label) })
      }
    }

    handleEdit = () => {
      this.setState({ editing: true })
    }

    handleCancel = () => {
      this.setState({
        editing: false,
        endUser: fromJS(this.props.endUser),
        enabledAttributes: this.getEnabledAttributes(),
      })
    }

    handleSave = () => {
      if (!this.hasChanged()) return
      this.props.actions.updateEndUser({
        endUserId: this.props.endUser.id,
        endUserParams: this.state.endUser && this.state.endUser.toJS()
      })
      this.props.actions.patchOrgProperties(
        patchOperations(
          { enabledAttributes: JSON.stringify(this.state.enabledAttributes && this.state.enabledAttributes.toJS()) },
          this.props.orgProperties.enabledAttributes
        )
      )
      this.setState({ editing: false })
    }

    createHandleInputChange = curry((path, newValue) => {
      this.setState({ endUser: this.state.endUser.setIn(path, newValue) })
    })

    handleLocationChange = async (suggestion) => {
      const { lat, lng } = suggestion.location
      const timezone = await fetchTimeZone(suggestion.location.lat, suggestion.location.lng)
      const city = suggestion.gmaps.address_components.find((component) => {
        return component.types.find((type) => {
          return type === 'locality'
        })
      })
      const country = suggestion.gmaps.address_components.find((component) => {
        return component.types.find((type) => {
          return type === 'country'
        })
      })
      this.setState({
        endUser: this.state.endUser
          .setIn(['attributes', 'display_location'], suggestion.label)
          .setIn(['attributes', 'latitude'], lat)
          .setIn(['attributes', 'longitude'], lng)
          .setIn(['attributes', 'city'], city.long_name)
          .setIn(['attributes', 'country'], country.long_name)
          .set('timeZone', timezone.timeZoneId)
      })
    }

    getEnabledAttributes() {
      return this.props.orgProperties.enabledAttributes
        ? Set(JSON.parse(this.props.orgProperties.enabledAttributes))
        : DEFAULT_ENABLED_ATTRIBUTES
    }

    userRenderer(userId) {
      if (typeof userId === 'undefined') return
      const { users } = this.props
      const user = users.get(userId) && users.get(userId).toJS()
      return (
        <div className="flex">
          <HoverAvatar user={user} size="extra-small" style={{ marginRight: 4 }}/>
          {getDisplayName(user)}
        </div>
      )
    }

    getContactRows() {
      const { endUser, editing } = this.state
      return [
        {
          label: LABEL.name,
          value: endUser.get('name'),
          handleChange: this.createHandleInputChange(['name']),
        },
        {
          label: LABEL.email,
          value: endUser.get('email'),
          handleChange: this.createHandleInputChange(['email']),
        },
        {
          label: LABEL.externalId,
          value: endUser.get('externalId'),
        },
        {
          label: LABEL.phone,
          value: endUser.get('phone'),
          handleChange: this.createHandleInputChange(['phone']),
        },
        {
          label: LABEL.timeZone,
          value: endUser.get('timeZone'),
        },
        {
          label: LABEL.displayLocation,
          value: (
            editing
              ? <LocationSearch
                  placeholder=""
                  initialValue={endUser.getIn(['attributes', 'display_location'])}
                  types={['(cities)']}
                  onSuggestSelect={this.handleLocationChange}
                />
              : <span className="flex-3 dsg-Body">{endUser.getIn(['attributes', 'display_location'])}</span>
          ),
          empty: !endUser.getIn(['attributes', 'display_location']),
        },
        {
          label: LABEL.employmentTitle,
          value: endUser.getIn(['attributes', 'employment_title']),
          handleChange: this.createHandleInputChange(['attributes', 'employment_title'])
        },
        {
          label: LABEL.employmentRole,
          value: endUser.getIn(['attributes', 'employment_role']),
          handleChange: this.createHandleInputChange(['attributes', 'employment_role'])
        },
        {
          label: LABEL.employmentSeniority,
          value: endUser.getIn(['attributes', 'employment_seniority']),
          handleChange: this.createHandleInputChange(['attributes', 'employment_seniority'])
        },
      ]
    }

    getSocialRows() {
      const { endUser } = this.state
      return [
        {
          label: LABEL.linkedinHandle,
          href: `http://linkedin.com/${endUser.getIn(['attributes', 'linkedin_handle'])}`,
          value: endUser.getIn(['attributes', 'linkedin_handle']),
          handleChange: this.createHandleInputChange(['attributes', 'linkedin_handle'])
        },
        {
          label: LABEL.twitterHandle,
          href: `http://twitter.com/${endUser.getIn(['attributes', 'twitter_handle'])}`,
          value: endUser.getIn(['attributes', 'twitter_handle']),
          handleChange: this.createHandleInputChange(['attributes', 'twitter_handle'])
        },
        {
          label: LABEL.bio,
          value: endUser.getIn(['attributes', 'bio']),
          handleChange: this.createHandleInputChange(['attributes', 'bio']),
        },
        {
          label: LABEL.githubHandle,
          value: endUser.getIn(['attributes', 'github_handle']),
          href: `http://github.com/${endUser.getIn(['attributes', 'github_handle'])}`,
          handleChange: this.createHandleInputChange(['attributes', 'github_handle']),
        },
        {
          label: LABEL.facebookHandle,
          value: endUser.getIn(['attributes', 'facebook_handle']),
          href: `http://facebook.com/${endUser.getIn(['attributes', 'facebook_handle'])}`,
          handleChange: this.createHandleInputChange(['attributes', 'facebook_handle']),
        },
      ]
    }

    handleTagsChange = tags => {
      this.setState({
        endUser: this.state.endUser.set('tags', List(tags.map(tag => tag.id)))
      })
    }

    handleAccountOwnerChange = ({ userId }) => {
      this.setState({
        endUser: this.state.endUser.setIn(['attributes', '_account_owner'], userId),
      })
    }

    getDriftRows() {
      const { endUserSegments, endUserConversationsCount, users } = this.props
      const { endUser, editing } = this.state
      const accountOwnerId = endUser.getIn(['attributes', '_account_owner'])
      const endUserTags = endUser.get('tags')
      return [
        {
          label: LABEL._account_owner,
          value: editing
            ? <UserSelect
                selectedUserId={accountOwnerId}
                onChange={this.handleAccountOwnerChange}
              />
            : this.userRenderer(parseInt(accountOwnerId)),
          empty: !users.get(parseInt(accountOwnerId)),
        },
        {
          label: LABEL.tags,
          value: editing
            ? <TagSelect onChange={this.handleTagsChange} selected={endUserTags && endUserTags.toJS()}/>
            : <TagList tagIds={endUserTags && endUserTags.toJS()}/>,
          empty: !(endUserTags && endUserTags.size),
        },
        {
          label: LABEL.conversations,
          value: _.get(endUserConversationsCount, 'all', 0),
        },
        {
          label: LABEL.segments,
          value: _.get(endUserSegments, 'length', 0),
        },
      ]
    }

    getCustomRows() {
      const { endUser, editing } = this.state
      const { endUserAttributesProperties } = this.props
      return endUserAttributesProperties
        .filter(({ name }) => !DEFAULT_ATTRIBUTES.has(name))
        .map(({ name, displayName, type }) => {
          const value = endUser.getIn(['attributes', name])

          return {
            value: type === 'DATETIME' && !editing && !!value
              ? <span title={value}>{moment(value).format('L LT')}</span>
              : endUser.getIn(['attributes', name]),
            label: displayName,
            handleChange: UNEDITABLE_ATTRIBUTES.has(name)
              ? undefined
              : this.createHandleInputChange(['attributes', name])
          }
        })
    }

    render() {
      const { editing, enabledAttributes } = this.state
      return (
        <ComposedComponent
          contactRows={this.getContactRows()}
          socialRows={this.getSocialRows()}
          driftRows={this.getDriftRows()}
          customRows={this.getCustomRows()}
          editing={editing}
          changed={this.hasChanged()}
          enabledAttributes={enabledAttributes}
          createAttributeCheckBoxChange={this.createAttributeCheckBoxChange}
          handleEdit={this.handleEdit}
          handleCancel={this.handleCancel}
          handleSave={this.handleSave}
          {...this.props}
        />
      )
    }
  }

  const mapStateToProps = (state, props) => {
    const propsToSelectFrom = { endUser: props.endUser }
    return {
      endUserAttributesFetched: EndUserAttributesSelectors.attributesFetched(state),
      endUserAttributesProperties: EndUserAttributesSelectors.properties(state),
      endUserSegments: EndUserSegmentsSelectors.endUserSegments(state, propsToSelectFrom),
      segmentFetchedForEndUser: EndUserSegmentsSelectors.segmentFetchedForEndUser(state, propsToSelectFrom),
      endUserConversationsCount: ConversationSelectors.endUserConversationsCount(state, propsToSelectFrom),
      endUserConversationsCountFetched: ConversationSelectors.endUserConversationsCountFetched(state, propsToSelectFrom),
      orgId: OrgSelector.orgId(state),
      orgProperties: OrgSelector.properties(state),
      users: UserSelector.orgUsersById(state)
    }
  }

  const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators({
      fetchEndUserAttributes: EndUserAttributesActions.fetchAttributes,
      fetchSegments: EndUserSegmentsActions.fetchSegments,
      fetchConversationsCountForEndUserId: ConversationActions.fetchConversationsCountForEndUserId,
      patchOrgProperties: OrgActions.patchOrgProperties,
      updateEndUser: EndUserActions.updateEndUser
    }, dispatch),
  })

  return connect(mapStateToProps, mapDispatchToProps)(GetRows)
}



// WEBPACK FOOTER //
// ./src/scripts/components/customer/EndUserAttributesCard/helper.js